import {MathUtils} from 'three'

/**
 * Time curves that `THREE.MathUtils` does not already have.
 *
 * Deliberately tiny. `clamp`, `lerp`, `damp`, `smoothstep`, `smootherstep` and
 * `inverseLerp` all exist there and must be imported from three rather than
 * reimplemented here - a second `clamp` in the codebase is how two subtly
 * different clamps end up shipping.
 *
 * What is missing is the asymmetric cubic pair. `smoothstep` is symmetric (slow
 * in AND slow out), which is the wrong shape for an impact: an impact has no
 * slow-in. Hence `easeOutCubic` for anything that fires on the tap, and
 * `easeInCubic` for anything that holds and then drops away at the end of its
 * own life.
 */

/** Fast start, decelerating stop. The impact curve. */
export const easeOutCubic = (progress: number): number => 1 - (1 - progress) ** 3

/**
 * Slow start, accelerating end.
 *
 * Used inverted (`1 - easeInCubic(p)`) to hold a value near full and then drop
 * it off a cliff. A shockwave whose brightness falls linearly while its area
 * grows reads as a slowly dying blob rather than a flash.
 */
export const easeInCubic = (progress: number): number => progress ** 3

/**
 * Clamped linear normalisation of `value` onto `[start, end] -> [0, 1]`.
 *
 * `MathUtils.smoothstep` clamps but also curves; `MathUtils.inverseLerp` is
 * linear but does not clamp. The timeline needs "linear and clamped", and
 * writing that inline at each beat is how one of them ends up unclamped.
 */
export const ramp01 = (value: number, start: number, end: number): number =>
  MathUtils.clamp(MathUtils.inverseLerp(start, end, value), 0, 1)
